import { useState, useEffect, Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import SimpleButton from '../common/SimpleButton'
import LoadingSpinner from '../common/LoadingSpinner'
import EmptyPageResults from '../common/EmptyPageResults'
import { toast } from '../common/Toast'
import ConfirmationModal from '../common/ConfirmationModal'
import TooltipedIcon from '../common/TooltipedIcon'
import { useWindowSize } from '../../libs/hooks'
import { CloseSimpleIcon, ExpandLessIcon, ExpandMoreIcon, InformationIcon, SuccessTick } from '../../icons'
import {
  deleteOnlineAccessAssociation,
  deleteProduct,
  getLatestProductCyclesBySn,
  getOnlineAccessAssociations,
  putOnlineAccessAssociation,
} from '../../utils/requests/productsAPI'
import { productsWritePermission, productsDeletePermission } from '../../utils/permissionValidation'
import './online-access.scss'

const OnlineAccess = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowSize()[0] <= 768
  const { serialNumber, email, isG4Device, isColteneAdmin, isAdmin, isJeniProduct } = props
  const [associations, setAssociations] = useState([])
  const [expandedAssociation, setExpandedAssociation] = useState(0)
  const [selectedUserUuid, setSelectedUserUuid] = useState(null)
  const [isDeviceWithCycles, setIsDeviceWithCycles] = useState(false)
  const [hasRetrievedCycles, setHasRetrievedCycles] = useState(false)
  const [showRemoveProductConfirmationModal, setShowRemoveProductConfirmationModal] = useState(false)
  const [showSpinner, setShowSpinner] = useState(null)
  const [isFirstLoad, setIsFirstLoad] = useState(true)

  useEffect(() => {
    if (serialNumber) {
      setShowSpinner(true)

      getAssociations(true)

      return
    }

    setIsFirstLoad(false)
  }, [serialNumber])

  const getAssociations = (isFirstLoad) => {
    getOnlineAccessAssociations({ sn: serialNumber })
      .then((data) => {
        data.sort((a, b) => (a.association_active < b.association_active ? 1 : -1))

        if (isFirstLoad && data.length > 0) {
          setExpandedAssociation(data[0].customer_email)
        }

        setAssociations(data)

        setShowSpinner(false)
        setIsFirstLoad(false)
      })
      .catch((e) => {
        if (e?.message !== 'NO_SESSION') {
          toast.error(t('error.failure_msg'))
        }

        setShowSpinner(false)
        setIsFirstLoad(false)
      })
  }

  const handleChangeAssociation = (targetUserEmail, associate) => {
    setShowSpinner(true)

    const changeAssociationRequestBody = {
      serial_number: serialNumber,
      email: targetUserEmail,
      action_by: email,
    }

    let changeAssociationPromise

    if (associate) {
      changeAssociationPromise = putOnlineAccessAssociation(changeAssociationRequestBody)
    } else {
      changeAssociationPromise = deleteOnlineAccessAssociation(changeAssociationRequestBody)
    }

    changeAssociationPromise
      .then(() => {
        getAssociations()
      })
      .catch((e) => {
        if (e?.message !== 'NO_SESSION') {
          toast.error(t('error.failure_msg'))
        }

        setShowSpinner(false)
      })
  }

  const handleCheckDeviceCycles = () => {
    if (!isColteneAdmin || hasRetrievedCycles) {
      setShowRemoveProductConfirmationModal(true)
      return
    }

    setShowSpinner(true)

    getLatestProductCyclesBySn(serialNumber, { limit: 1 })
      .then((result) => {
        setIsDeviceWithCycles(result.data?.length > 0)
        setHasRetrievedCycles(true)

        setShowRemoveProductConfirmationModal(true)
      })
      .finally(() => {
        setShowSpinner(false)
      })
  }

  const handleDeleteProduct = () => {
    setShowSpinner(true)

    deleteProduct(selectedUserUuid, serialNumber)
      .then(() => {
        const filteredAssociations = associations.filter((association) => association.user_uuid !== selectedUserUuid)
        setAssociations(filteredAssociations)

        toast.success(t('device_context.unit.remove_success'))
      })
      .catch((e) => {
        toast.error(t([`error.${e.response?.data?.code}`, t('error.failure_msg')]))
      })
      .finally(() => {
        setShowSpinner(false)
      })
  }

  const toggleExpandAssociation = (customerEmail) => {
    setExpandedAssociation(customerEmail === expandedAssociation ? null : customerEmail)
  }

  return (
    <div className="online-access-wrapper">
      {associations.map((association, index) => (
        <Fragment key={`association-${index}`}>
          <div
            className={`association-expand-header d-flex flex-justify-between flex-align-center ${association.customer_email === expandedAssociation ? 'highlight' : 'border-bottom'}`}
            onClick={() => toggleExpandAssociation(association.customer_email)}
          >
            <div className="title">
              <div>{association.customer_email}</div>
            </div>
            <div className="icon">{expandedAssociation === association.customer_email ? <ExpandLessIcon /> : <ExpandMoreIcon />}</div>
          </div>
          {expandedAssociation === association.customer_email && (
            <div className="association-expand-body">
              <div className="data">
                <div className="label">{t('device_context.unit.warranty_registered')}: </div>
                <div className="label-data">{association.warranty_registered ? <SuccessTick /> : <CloseSimpleIcon className="close-icon" />}</div>
              </div>
              <div className="data">
                <div className="label">{t('device_context.unit.email_sent_by_unit')}: </div>
                <div className="label-data">{association.email_sent_by_unit ? <SuccessTick /> : <CloseSimpleIcon className="close-icon" />}</div>
              </div>
              <div className="data">
                <div className="label">{t('device_context.unit.log_in_succesfull')}: </div>
                <div className="label-data">{association.email_link_validation ? <SuccessTick /> : <CloseSimpleIcon className="close-icon" />}</div>
              </div>
              <div className="data">
                <div className="label">{t('device_context.unit.allow_button_clicked_on_unit')}: </div>
                <div className="label-data">{association.association_active ? <SuccessTick /> : <CloseSimpleIcon className="close-icon" />}</div>
              </div>
              <div className="data">
                <div className="label">{t('device_context.unit.association_status')}: </div>
                <div className="label-data">{association.association_active ? <SuccessTick /> : <CloseSimpleIcon className="close-icon" />}</div>
              </div>
              <div className="data">
                <div className="label">{t('device_context.unit.associated_to')}: </div>
                <div className="label-data with-shrink">{association.current_associated_to || t('n_a')}</div>
                {association.association_active && association.branchMembers?.length > 0 && (
                  <div className="label-note">
                    <TooltipedIcon
                      icon={<InformationIcon />}
                      tooltipTitle={t('organization_members')}
                      tooltipText={association.branchMembers.join(' ')}
                      orientation={'top'}
                      {...(isMobile && { orientation: 'left' })}
                    />
                  </div>
                )}
              </div>
              <div className="data">
                <div className="label">
                  {association.association_active ? t('device_context.unit.associated_by') : t('device_context.unit.dissociated_by')}:{' '}
                </div>
                <div className="label-data">
                  {association.association_active ? association.associated_by || t('n_a') : association.disassociated_by || t('n_a')}
                </div>
              </div>
              {(!isJeniProduct || isAdmin) && (
                <div className="data">
                  <div className="label button-label" />
                  <div className="label-data button-label-data">
                    <SimpleButton
                      className={'action-cancel-button remove-button'}
                      onClick={() => {
                        setSelectedUserUuid(association.user_uuid)
                        handleCheckDeviceCycles()
                      }}
                      disabled={showSpinner || association.association_active}
                      requiredPermission={productsDeletePermission}
                      title={t('device_context.delete_from_user_account')}
                    >
                      {t('device_context.delete_from_account')}
                    </SimpleButton>

                    <SimpleButton
                      className={(association.association_active ? 'cancel-button' : 'submit-button') + ' association-button'}
                      onClick={() => handleChangeAssociation(association.customer_email, !association.association_active)}
                      disabled={
                        showSpinner || !isG4Device || (!association.association_active && associations.find((association) => association.association_active))
                      }
                      requiredPermission={productsWritePermission}
                    >
                      {association.association_active ? t('device_context.unit.dissociate') : t('device_context.unit.associate')}
                    </SimpleButton>
                  </div>
                </div>
              )}
            </div>
          )}
        </Fragment>
      ))}
      {!isFirstLoad && associations.length === 0 && <EmptyPageResults className="empty-list" title={t('no_data_available')} />}
      {showRemoveProductConfirmationModal && (
        <ConfirmationModal
          onToggle={() => setShowRemoveProductConfirmationModal(false)}
          message={`${isDeviceWithCycles ? t('device_context.unit.device_has_cycles_linked') : ''} ${t('device_context.unit.remove_product_confirmation')}`}
          onCancel={() => setShowRemoveProductConfirmationModal(false)}
          onAccept={() => {
            handleDeleteProduct()
            setShowRemoveProductConfirmationModal(false)
          }}
          requiredPermission={productsDeletePermission}
          isWarning
        />
      )}
      {showSpinner && (
        <div className="spinner-wrapper">
          <LoadingSpinner />
        </div>
      )}
    </div>
  )
}

export default OnlineAccess

